import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { KeycloakService } from 'keycloak-angular';
import { AuthApiService, UserData } from '@angular-workspace/services';
import { HeaderComponent } from '../../shared/components/header/header.component';
import { FooterComponent } from '../../shared/components/footer/footer.component';
import { GoogleAnalyticsService } from '../../services/google-analytics.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-contact-us',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    HeaderComponent,
    FooterComponent,
  ],
  templateUrl: './contact-us.component.html',
  styleUrl: './contact-us.component.scss',
})
export class ContactUsComponent implements OnInit {
  private http = inject(HttpClient);
  private googleAnalytics = inject(GoogleAnalyticsService);
  private keycloak = inject(KeycloakService);
  private authApiService = inject(AuthApiService);
  private cdr = inject(ChangeDetectorRef);
  /** Used when no contactFormActionUrl is set (mailto fallback) */
  contactEmail = (environment as { contactFormEmail?: string }).contactFormEmail ?? 'sunil.babu@datakaveri.org';
  /** API URL from environment (e.g. Zoho /records). When set, submit POSTs here via HttpClient. */
  contactFormActionUrl = (environment as { contactFormActionUrl?: string }).contactFormActionUrl ?? '';
  prefillFirstName = '';
  prefillLastName = '';
  prefillEmail = '';
  /** Client-side validation for Zoho-native form (textarea); shown when submit blocked. */
  zohoFormMultiLineError: string | null = null;
  readonly zohoRequirementMinLength = 10;
  contactForm: FormGroup;
  submitted = false;
  submitSuccess = false;
  submitError: string | null = null;
  isSubmitting = false;

  /** URL Zoho redirects to after form submit (thank-you page). */
  get thankYouRedirectUrl(): string {
    if (typeof window !== 'undefined' && window.location?.origin) {
      return `${window.location.origin}/contact-us/thank-you`;
    }
    return '/contact-us/thank-you';
  }

  constructor(private fb: FormBuilder) {
    this.contactForm = this.fb.group({
      firstName: ['', [Validators.required, Validators.minLength(1)]],
      lastName: ['', [Validators.required, Validators.minLength(1)]],
      email: ['', [Validators.required, Validators.email]],
      subject: ['', [Validators.required, Validators.minLength(3)]],
      message: ['', [Validators.required, Validators.minLength(10)]],
    });
  }

  async ngOnInit(): Promise<void> {
    await this.prefillLoggedInUserDetails();
  }

  onZohoFormSubmit(event: SubmitEvent): void {
    const formEl = event.target as HTMLFormElement;
    const textarea = formEl?.querySelector<HTMLTextAreaElement>('textarea[name="MultiLine"]');
    const raw = textarea?.value ?? '';
    const trimmed = raw.trim();
    this.zohoFormMultiLineError = null;

    if (!trimmed) {
      event.preventDefault();
      this.zohoFormMultiLineError = 'Tell us more about your requirement.';
      textarea?.focus();
      return;
    }
    if (trimmed.length < this.zohoRequirementMinLength) {
      event.preventDefault();
      this.zohoFormMultiLineError = `Enter at least ${this.zohoRequirementMinLength} characters.`;
      textarea?.focus();
      return;
    }

    // Normalize so Zoho receives trimmed text (avoids whitespace-only passing HTML5 required).
    if (textarea && raw !== trimmed) {
      textarea.value = trimmed;
    }
  }

  onZohoMultiLineInput(): void {
    this.zohoFormMultiLineError = null;
  }

  get f() {
    return this.contactForm.controls;
  }

  onSubmit(): void {
    this.submitted = true;
    this.submitSuccess = false;
    this.submitError = null;
    if (this.contactForm.invalid) {
      return;
    }
    const v = this.contactForm.value;
    const actionUrl = (this.contactFormActionUrl || '').trim();
    if (actionUrl) {
      this.submitViaApi(actionUrl, v);
    } else {
      this.submitViaMailto(v);
    }
  }

  /**
   * POST form data to the configured API URL (e.g. Zoho /records). Stays on page; no email client.
   * Field names match Zoho ContactUs form (Name_First, Name_Last, Email, MultiLine); adjust if your form differs.
   */
  private submitViaApi(actionUrl: string, v: Record<string, string>): void {
    this.isSubmitting = true;
    this.submitError = null;
    const multiLine = [v['subject'] ? `Subject: ${v['subject']}` : '', v['message'] ?? ''].filter(Boolean).join('\n\n');
    const body = new URLSearchParams({
      Name_First: v['firstName'] ?? '',
      Name_Last: v['lastName'] ?? '',
      Email: v['email'] ?? '',
      Dropdown: v['subject'] ?? 'General',
      MultiLine: multiLine,
    }).toString();
    this.http
      .post(actionUrl, body, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
        responseType: 'json',
      })
      .subscribe({
        next: () => {
          this.googleAnalytics.trackContactFormSubmit();
          this.submitSuccess = true;
          this.contactForm.reset();
          this.submitted = false;
          this.isSubmitting = false;
        },
        error: (err) => {
          this.submitError = err?.message || 'Failed to send message. Please try again.';
          this.isSubmitting = false;
        },
      });
  }

  private submitViaMailto(v: Record<string, string>): void {
    const fullName = [v['firstName'], v['lastName']].filter(Boolean).join(' ');
    const body = [
      `Name: ${fullName}`,
      `Email: ${v['email']}`,
      '',
      v['message'],
    ].join('\n');
    const subject = encodeURIComponent(v['subject']);
    const bodyEnc = encodeURIComponent(body);
    this.googleAnalytics.trackContactFormSubmit();
    window.location.href = `mailto:${this.contactEmail}?subject=${subject}&body=${bodyEnc}`;
    this.submitSuccess = true;
    this.contactForm.reset();
    this.submitted = false;
  }

  private async prefillLoggedInUserDetails(): Promise<void> {
    const isLoggedIn = await this.isUserAuthenticated();
    if (!isLoggedIn) {
      return;
    }

    // 1) Try live user API first.
    try {
      const userData: UserData = await this.authApiService.getUserInfo();
      this.applyPrefillFromUserObject(userData?.['result'] || userData || {});
    } catch (error) {
      console.warn('Unable to prefill from users API, trying fallback sources:', error);
    }

    // 2) Fallback: locally cached userData written by AuthListener service.
    if (!this.hasAnyPrefill()) {
      this.applyPrefillFromLocalStorage();
    }

    // 3) Last fallback: Keycloak profile.
    if (!this.hasAnyPrefill()) {
      await this.applyPrefillFromKeycloakProfile();
    }
  }

  private async isUserAuthenticated(): Promise<boolean> {
    try {
      if (await this.keycloak.isLoggedIn()) {
        return true;
      }
    } catch {
      // ignore and continue with fallback check
    }

    const keycloakInstance = this.keycloak.getKeycloakInstance();
    return !!(keycloakInstance?.authenticated && keycloakInstance?.token);
  }

  private hasAnyPrefill(): boolean {
    return !!(this.prefillFirstName || this.prefillLastName || this.prefillEmail);
  }

  private applyPrefillFromLocalStorage(): void {
    try {
      const raw = localStorage.getItem('userData');
      if (!raw) {
        return;
      }
      const parsed = JSON.parse(raw);
      const result = parsed?.result || parsed || {};
      this.applyPrefillFromUserObject(result);
    } catch {
      // Ignore malformed localStorage data.
    }
  }

  private async applyPrefillFromKeycloakProfile(): Promise<void> {
    try {
      const profile = await this.keycloak.loadUserProfile();
      const firstName = (profile?.firstName || '').trim();
      const lastName = (profile?.lastName || '').trim();
      const email = (profile?.email || '').trim();
      this.setPrefillValues(firstName, lastName, email);
    } catch {
      // Ignore Keycloak profile errors.
    }
  }

  private applyPrefillFromUserObject(result: Record<string, any>): void {
    const firstName =
      (result['givenName'] ||
        result['firstName'] ||
        result['first_name'] ||
        result['first_name_en'] ||
        '')
        .toString()
        .trim();
    const lastName =
      (result['familyName'] ||
        result['lastName'] ||
        result['last_name'] ||
        result['last_name_en'] ||
        '')
        .toString()
        .trim();
    const email = (result['email'] || '').toString().trim();

    let resolvedFirstName = firstName;
    let resolvedLastName = lastName;

    if ((!resolvedFirstName || !resolvedLastName) && result['name']) {
      const nameParts = String(result['name']).trim().split(/\s+/).filter(Boolean);
      if (nameParts.length >= 2) {
        resolvedFirstName = resolvedFirstName || nameParts[0];
        resolvedLastName = resolvedLastName || nameParts[nameParts.length - 1];
      } else if (nameParts.length === 1) {
        resolvedFirstName = resolvedFirstName || nameParts[0];
      }
    }

    this.setPrefillValues(resolvedFirstName, resolvedLastName, email);
  }

  private setPrefillValues(firstName: string, lastName: string, email: string): void {
    if (firstName) {
      this.prefillFirstName = firstName;
    }
    if (lastName) {
      this.prefillLastName = lastName;
    }
    if (email) {
      this.prefillEmail = email;
    }
    // Keycloak/Auth promises can resolve outside Angular zone; force UI refresh.
    setTimeout(() => {
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    }, 0);
  }
}
